import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';
import { GroupLookupItem, LookupItem, StringGroupLookupItem, StringLookupItem } from '@models/lookup';
import { removeCommasFromValue } from '@shared/helpers/functions/helpers';
import { MatInput } from '@angular/material/input';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import {
    BooleanFilter,
    DateFilter,
    DateRangeFilter,
    Filter,
    FilterTypes,
    MultilineTextFilter,
    MultiSelectFilter,
    NumberFilter,
    SelectFilter,
    StringFilter,
    TimeFilter,
} from '@models/filter-types';
import { FilterInputErrorStateMatcher } from '@shared/components/filters/filter-input-error-state-matcher';

type SupportedTypes = StringLookupItem | LookupItem | GroupLookupItem | StringGroupLookupItem;

@Component({
    selector: 'app-filter-input',
    templateUrl: './filter-input.component.html',
    styleUrls: ['./filter-input.component.scss'],
    providers: [{ useValue: { overlayPanelClass: 'select-overlay' }, provide: MAT_SELECT_CONFIG }],
    standalone: false,
})
export class FilterInputComponent implements OnInit, OnChanges, AfterViewInit {
    @Input() overrideDisabled!: boolean;
    @Input() preventTab = false;
    @Input() collapsible = false;
    @Input() appearance?: 'outline' | 'fill';
    @Input() subscriptSizing: 'fixed' | 'dynamic' = 'fixed';
    @Input() showErrorsAfterInteraction = false;
    @Input() compactDateRange = false;
    @Input() property!:
        | SelectFilter<SupportedTypes>
        | MultiSelectFilter<SupportedTypes>
        | NumberFilter
        | StringFilter
        | DateFilter
        | DateRangeFilter
        | BooleanFilter
        | TimeFilter
        | MultilineTextFilter
        | Filter<SupportedTypes>;
    @Input() disabledOptions!: SupportedTypes[];
    @Input() hideOptions!: SupportedTypes[];

    @Output() filterRef: EventEmitter<unknown> = new EventEmitter<unknown>();
    @Output() optionDeleteButtonClicked: EventEmitter<unknown> = new EventEmitter<unknown>();
    @Output() valueChanged: EventEmitter<unknown> = new EventEmitter<unknown>();
    @Output() keyDownEvent: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>();

    @ViewChild('search') searchTextBox!: ElementRef;
    @ViewChild('inputFilter') inputFilterHtml!: MatInput;

    disabled = false;

    inputType!: string;

    selectedValues: SupportedTypes[] = [];

    errorStateMatcher!: FilterInputErrorStateMatcher<unknown>;

    protected readonly parseFloat = parseFloat;

    constructor() {}

    /** a cleared or partial entry is null, never NaN — NaN stored in
     * Value crashes the template's DecimalPipe (NG02100) on the next
     * change-detection pass */
    parseNumberValue(value: string): number | null {
        const parsed = parseFloat(removeCommasFromValue(value));
        return Number.isNaN(parsed) ? null : parsed;
    }

    ngOnInit() {
        this.inputType = this.getInputType();
    }

    ngOnChanges() {
        this.disabled = this.property?.isReadonly || this.overrideDisabled;
        this.errorStateMatcher = new FilterInputErrorStateMatcher<SupportedTypes>(this.property, this.showErrorsAfterInteraction);
    }

    ngAfterViewInit(): void {
        if (this.inputFilterHtml) {
            this.filterRef.emit(this.inputFilterHtml);
            this.property.filterRef = this.inputFilterHtml;
        }
    }

    getInputType(): string {
        switch (this.property?.Type) {
            case FilterTypes.StringFilterType:
                return 'text';
            case FilterTypes.NumberFilterType:
                return 'number';
            case FilterTypes.DateFilterType:
                return 'date';
            default:
                return undefined;
        }
    }

    /**
     * Remove from selected values based on uncheck
     */
    // @ts-expect-error TS7053: implicit any
    selectionChange(event) {
        if (event.isUserInput && event.source.selected === false) {
            this.selectedValues.forEach((value, index) => {
                if (value.name === event.source.value.name && value.id === event.source.value.id) {
                    this.selectedValues.splice(index, 1);
                }
            });
        }
    }

    removeCommasFromValue(value: string): string {
        return removeCommasFromValue(value);
    }
}
